import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { API_BASE_URL } from '../../api';

interface PopupConfig {
  enabled: boolean;
  image_url: string;
  text: string;
}

const WelcomePopup: React.FC = () => {
  const [config, setConfig] = useState<PopupConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const alreadySeen = sessionStorage.getItem('welcome_popup_seen');
    if (alreadySeen === 'true') return;

    fetchPopupConfig();
  }, []);

  const fetchPopupConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/welcome-popup`);
      if (response.ok) {
        const data = await response.json();
        if (data.enabled && data.image_url) {
          setConfig(data);
          // Small delay for smoother entrance
          setTimeout(() => setIsVisible(true), 300);
        }
      }
    } catch (error) {
      console.error('Failed to fetch welcome popup config', error);
    }
  };

  const handleClose = useCallback(() => {
    setIsVisible(false);
    sessionStorage.setItem('welcome_popup_seen', 'true');
    // Remove from DOM after animation
    setTimeout(() => setConfig(null), 300);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, handleClose]);

  if (!config) return null;

  const imageUrl = config.image_url.startsWith('/api')
    ? `${API_BASE_URL}${config.image_url}`
    : config.image_url;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Popup d'accueil"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-md transition-colors"
          aria-label="Fermer"
        >
          <X size={20} className="text-gray-700" />
        </button>

        {/* Image */}
        <div className="w-full">
          <img
            src={imageUrl}
            alt="Affiche d'accueil"
            loading="lazy"
            className="w-full h-auto max-h-[70vh] object-contain"
          />
        </div>

        {/* Optional text */}
        {config.text && (
          <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-accent-sage/10">
            <p className="font-nunito text-neutral-body text-center text-sm md:text-base leading-relaxed">
              {config.text}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePopup;
